import type { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import type { DragEventHandler } from 'react';
import type {
	ControllerRenderProps,
	FieldPath,
	// FieldPathValue,
	FieldValues,
	// PathValue,
	// UseControllerProps,
} from 'react-hook-form';

// import byteSize from 'byte-size';
import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import { faFile, faXmark } from '@fortawesome/pro-light-svg-icons';
import { ErrorMessage } from '@hdir/ui/error-message';
import { Link } from '@hdir/ui/link';
import byteSize from 'byte-size';
import React, {
	forwardRef, useEffect, useImperativeHandle, useId, useRef, useState,
} from 'react';
import { useController } from 'react-hook-form';

import { forceArray } from '/lib/helsecore/forceArray';
// import { Field } from '/react4xp/shared/Field';

import { customTwMerge } from '../../util/customTwMerge';
import { isSet } from '../../../app/overdosefare/isSet';
import { FontAwesomeIconWrapper } from '../../components/FontAwesomeIconWrapper';
import { Button } from '../Button';
import { Div, Label, Span, cn } from '../Element';
import { Row, Stack } from '../Flex';

import { useFormContext } from './FormContext';

// export function ImagePreviewer({
// 	file
// }: {
// 	file: File
// }): JSX.Element | null {
// 	if (!file || !['image/jpeg', 'image/png'].includes(file.type)) {
// 		return null;
// 	}
// 	const [base64DataUrl, setBase64DataUrl] = useState('');
// 	const reader = new FileReader();
// 	reader.onloadend = () => {
// 		if (typeof reader.result === 'string') {
// 			setBase64DataUrl(reader.result);
// 		} else {
// 			console.error("Reader result is not a string")
// 		}
// 	};

// 	reader.onerror = () => {
// 		console.error('There was an error reading the file!');
// 	}

// 	reader.readAsDataURL(file);

// 	return base64DataUrl ? <img alt="Uploaded Image" src={base64DataUrl}/> : null;
// }


interface FileUploadButtonProps<
	TName extends FieldPath<FieldValues> = FieldPath<FieldValues>,
> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'accept'|'name'|'value'> {
	accept?: MimeType[];
	buttonText?: string;
	inputId: string;
	labelId: string;
	max?: number;
	name: TName;
	perFileSizeLimit?: number;
	setErrors: React.Dispatch<React.SetStateAction<Error[]>>;
	totalSizeLimit?: number;
	uploadHelpLabelId: string;
	useControllerOnChange: ControllerRenderProps['onChange'];
	value?: File | File[];
}

type MimeType =
	| 'application/pdf'
	| 'image/gif'
	| 'image/jpeg'
	| 'image/png';

interface FileUploadProps<
	TName extends FieldPath<FieldValues> = FieldPath<FieldValues>,
> extends Omit<
		FileUploadButtonProps<TName>,
	| 'inputId'
	| 'labelId'
	| 'setErrors'
	| 'uploadHelpLabelId'
	| 'useControllerOnChange'
	> {
	boxText?: string;
	description?: string;
	errorSummaryLabel?: string;
	fileListHeading?: string;
	icon?: IconDefinition;
	label?: string;
	subtleText?: string;
}

interface Error {
	id: string;
	message: string;
}


// Used both for input.onChange and when dragNdropping files.
const doChange = ({
	accept,
	currentValue,
	max,
	multiple,
	name,
	newFiles,
	perFileSizeLimit,
	totalSizeLimit,
	setErrors,
	useControllerOnChange,
}: {
	accept?: MimeType[];
	currentValue?: File | File[];
	max?: number;
	multiple?: boolean;
	name: string;
	newFiles: File[];
	setErrors: React.Dispatch<React.SetStateAction<Error[]>>;
	perFileSizeLimit?: number;
	totalSizeLimit?: number;
	useControllerOnChange: ControllerRenderProps['onChange'];
}): void => {
	const newErrors: Error[] = [];
	let newValueArray: File[] = currentValue ? forceArray(currentValue) : [];

	if (isSet(perFileSizeLimit) && isSet(totalSizeLimit) && perFileSizeLimit > totalSizeLimit) {
		console.warn(`perFileSizeLimit:${perFileSizeLimit} > totalSizeLimit:${totalSizeLimit}! Setting perFileSizeLimit to totalSizeLimit.`);
		perFileSizeLimit = totalSizeLimit;
	}

	let totalSize = 0;
	for (const currentFile of newValueArray) {
		totalSize += currentFile.size;
	}

	// Given single, and dropping multiple files, display an error.
	if (!multiple && newFiles.length !== 1) {
		newErrors.push({
			id: name,
			message: `Filene ble ikke lastet opp fordi kun en fil er tillatt!`,
		});
	} else {
		for (const newFile of newFiles) {
			// console.debug('totalSize before', totalSize);
			if (accept?.length && !accept.includes(newFile.type as MimeType)) {
				newErrors.push({
					id: name,
					message: `Filen "${newFile.name}" ble ikke lastet opp fordi types ${newFile.type} ikke er tillatt.`,
				});
			} else if (perFileSizeLimit && newFile.size > perFileSizeLimit) {
				newErrors.push({
					id: name,
					message: `Filen "${newFile.name}" ble ikke lastet opp fordi den er over ${byteSize(perFileSizeLimit).toString()}.`,
				});
			} else if (totalSizeLimit && totalSize + newFile.size > totalSizeLimit) {
				newErrors.push({
					id: name,
					message: `Filen "${newFile.name}" ble ikke lastet opp fordi filene til sammen er over ${byteSize(totalSizeLimit).toString()}.`,
				});
			} else if (newValueArray.map(({name}) => name).includes(newFile.name)) {
				newErrors.push({
					id: name,
					message: `Filen "${newFile.name}" ble ikke lastet opp fordi den allerede er lastet opp.`,
				});
			} else {
				if (multiple) {
					if (max && newValueArray.length >= max) {
						newErrors.push({
							id: name,
							message: `Filen "${newFile.name}" ble ikke lastet opp fordi kun ${max} fil(er) er tillatt.`,
						});
					} else {
						newValueArray.push(newFile);
						totalSize += newFile.size;
					}
				} else { // single
					// Given single, and selecting a new file via the button, the previous file should be replaced.
					// Given single, and dropping single file, the previous file should be replaced.
					// if (newFiles.length === 1) {
					newValueArray = [newFile];
					totalSize += newFile.size;
					// } else {
					// 	// Handled before the for loop
					// }
				}
			}
			// console.debug('totalSize after', totalSize);
		} // for
	}

	setErrors(newErrors);

	const newValue = multiple ? newValueArray : newValueArray[0];

	useControllerOnChange(newValue);
};


const FileUploadButton = forwardRef<HTMLInputElement, FileUploadButtonProps>(({
	capture,
	multiple,

	accept,
	buttonText = capture ? 'Ta bilde'
		: multiple ? 'Last opp filer'
			: 'Last opp fil',
	className,
	disabled,
	inputId,
	labelId,
	max,
	name,
	perFileSizeLimit,
	setErrors,
	totalSizeLimit,
	uploadHelpLabelId,
	useControllerOnChange,
	value,
	...otherInputElementProps
}, ref) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	// @ts-expect-error Only covering click, not all HTMLInputElement
	useImperativeHandle(ref, () => ({
		click: () => {
			fileInputRef.current?.click();
		},
	}));
	return (
		<Div className='relative'>
			<input
				{...otherInputElementProps}
				accept={accept?.join(',')}
				aria-labelledby={labelId}
				aria-describedby={uploadHelpLabelId}
				capture={capture}
				className={customTwMerge('absolute h-0 opacity-0 width-0', className)}
				disabled={disabled}
				id={inputId}
				multiple={multiple}
				name={name}
				onChange={(syntheticBaseEvent) => {
					if (syntheticBaseEvent.target.files) {
						doChange({
							accept,
							currentValue: value,
							max,
							multiple,
							name,
							newFiles: Array.from(syntheticBaseEvent.target.files),
							perFileSizeLimit,
							setErrors,
							totalSizeLimit,
							useControllerOnChange,
						});
					}
				}}
				ref={fileInputRef}
				tabIndex={-1}
				type='file'
			/>
			<Button
				aria-describedby={uploadHelpLabelId}
				disabled={disabled}
				onClick={
					() => {
						fileInputRef.current?.click();
					}
				}
				variant='secondary'
			>
				{buttonText}
			</Button>
		</Div>
	);
},
);


export function FileUpload<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	accept = [],
	multiple,
	buttonText = multiple ? 'Last opp filer' : 'Last opp fil',
	boxText = multiple
		? `Dra og slipp filer her eller trykk "${buttonText}"`
		: `Dra og slipp fil her eller trykk "${buttonText}"`,
	capture,
	description,
	disabled: disabledProp,
	errorSummaryLabel = isStringLiteral(boxText) ? boxText : undefined,
	fileListHeading = 'Lastet opp ($count)',
	icon = faFile,
	label = multiple ? 'Last opp filer' : 'Last opp fil',
	max,
	name,
	// ref: refProp,
	perFileSizeLimit,
	subtleText,
	totalSizeLimit,
	...otherInputElementProps
}: FileUploadProps<TName>): React.JSX.Element {
	const inputId = useId();
	const labelId = useId();
	const dropZoneLabelId = useId();
	const fileListLabelId = useId();
	const uploadHelpLabelId = useId();

	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const [errors, setErrors] = useState<Error[]>([]);

	const {
		control,
		getRequired,
		setErrorSummaryLabel,
	} = useFormContext<TFieldValues>();

	const required: boolean = getRequired(name);
	const {
		field: {
			disabled,
			onChange: useControllerOnChange,
			ref,
			value,
		},
	} = useController<TFieldValues, TName>({
		control,
		disabled: disabledProp,
		name,
	});

	const valueArray = forceArray(value) as File[];

	const handleDrop: DragEventHandler<HTMLElement> = (event) => {
		event.preventDefault();
		if (disabled) { return; }
		setIsDraggingOver(false);
		doChange({
			accept,
			currentValue: valueArray,
			max,
			multiple,
			name,
			newFiles: Array.from(event.dataTransfer.files),
			perFileSizeLimit,
			setErrors,
			totalSizeLimit,
			useControllerOnChange,
		});
	};

	const handleDragOver: DragEventHandler<HTMLElement> = (event) => {
		event.preventDefault();
		if (disabled) { return; }
		setIsDraggingOver(true);
	};

	const handleDragLeave = (): void => {
		if (disabled) { return; }
		setIsDraggingOver(false);
	};

	useEffect(() => {
		if (errorSummaryLabel) {
			setErrorSummaryLabel({field: name, label: errorSummaryLabel});
		}
	},[errorSummaryLabel]);

	return (
		<Stack className='gap-xxs tablet:gap-xs'>
			{
				label // This check is needed because the Label below always have children, so Element does NOT return null.
					? <Label
						color={disabled ? 'textNeutralDisabled' : undefined}
						htmlFor={inputId}
						id={labelId}
						textSize='xl'
						weight='semiBold'
					>
						{label}
						<Span color={disabled ? 'textNeutralDisabled' : 'iconFeedbackErrorDefault'}>
							{required ? ' *' : null}
						</Span>
					</Label>
					: null
			}

			<Span textSize='sm'>{description}</Span>

			{/*
				Accessible drop zone
				https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/region_role
				Every element with a region role should include a label that describes the purpose
				of the content in the region, preferably with an aria-labelledby referencing a
				visible header. If no visible appropriate header is present, aria-label should be
				used.

				Using the <section> element will automatically communicate a section has a role of
				region if it is given an accessible name. Developers should always prefer using the
				correct semantic HTML element, in this case <section>, over using ARIA.

				https://developer.mozilla.org/en-US/docs/Glossary/Accessible_name
			*/}
			<Stack
				aria-labelledby={dropZoneLabelId} // Accessible_name
				aria-describedby={uploadHelpLabelId}
				as='section' // preferred over role='region'
				bgColor={isDraggingOver ? 'surfaceActionSecondaryHover' : 'surfaceActionSecondary'}
				borderColor={
					disabled ? 'actionSecondaryDisabled'
						: errors.length ? 'feedbackErrorDefault'
							: 'actionSecondaryDefault'
				}
				className={[
					'border-dashed',
					'border-md',
					'gap-sm',
					'items-center',
					'rounded-md',
					"text-center",
					// disabled ? 'cursor-no-drop' : undefined, // TODO
					disabled ? undefined : 'hover:bg-surface-action-secondary-hover',
				]}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				padding={{
					block: 'lg',
					inline: 'xxl',
				}}
				textSize='3xl' // This achieves the correct icon size
				tablet={{ width: '340px' }}
			>
				<FontAwesomeIconWrapper
					className={cn({
						color: disabled ? 'iconActionSecondaryDisabled' : 'iconActionSecondaryDefault',
					})}
					icon={icon}
				/>
				<Span
					color={disabled ? 'textActionSecondaryDisabled' : undefined}
					id={dropZoneLabelId}
					textSize='sm'
				>
					{boxText}
				</Span>
				<FileUploadButton
					{...otherInputElementProps}
					accept={accept}
					buttonText={buttonText}
					capture={capture}
					disabled={disabled}
					inputId={inputId}
					labelId={labelId}
					max={max}
					multiple={multiple}
					name={name}
					perFileSizeLimit={perFileSizeLimit}
					ref={ref}
					setErrors={setErrors}
					totalSizeLimit={totalSizeLimit}
					uploadHelpLabelId={uploadHelpLabelId}
					useControllerOnChange={useControllerOnChange}
					value={value}
				/>
			</Stack>

			<Span
				id={uploadHelpLabelId}
				className={[
					'color-text-neutral-subtle',
					// '!color-text-neutral-subtle',
					// 'text-neutral-subtle', // I think tailwind merge removes this.
					'text-sm',
				]}
			>
				{subtleText}
			</Span>

			{/*
				Live region – announces errors
				https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Guides/Live_regions
			*/}
			{
				<Stack
					aria-label='Liste over feil'
					aria-live="polite"
					as='section' // preferred over role='region'
					className={errors.length ? '' : 'hidden'} // No gap when empty :)
					forceRender
				>
					{
						errors.length
							? errors.map(({
								id,
								message,
							}, i) => <ErrorMessage
								id={id}
								key={i}
								onClick={() => {
									const newErrors = [...errors];
									newErrors.splice(i, 1);
									setErrors(newErrors);
								}}
							>
								{message}
							</ErrorMessage>)
							: null
					}
				</Stack>
			}

			{/*
				Live region – announces selected files
				https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Guides/Live_regions
			*/}
			<Stack
				aria-labelledby={fileListLabelId}
				aria-live="polite"
				as='section' // preferred over role='region'
			>
				<p
					id={fileListLabelId}
				>
					{fileListHeading.replace('$count', `${valueArray.length}`)}
				</p>
				{
					valueArray.length
						? valueArray.map((file, i) => <Row className='gap-sm' key={i}>
							<Link>{file.name}</Link>
							<FontAwesomeIconWrapper
								className={cn({
									color: disabled ? 'iconActionSecondaryDisabled' : 'iconActionSecondaryDefault',
								})}
								icon={faXmark}
								onClick={() => {
									const newValueArray = [...valueArray];
									newValueArray.splice(i, 1);
									const newValue = multiple ? newValueArray : newValueArray[0];
									useControllerOnChange(newValue);
								}}
							/>
						</Row>)
						: null
				}
			</Stack>
		</Stack>
	);

	// reader.onerror = () => {
	// 	console.error('There was an error reading the file!');
	// }

	// new Date(item.lastModified).toLocaleDateString()
	// <ImagePreviewer file={file}/>
}
